import { Component, OnInit, OnChanges, Input, Output, EventEmitter, HostListener, ViewChild, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { NavigationComponent } from '../navigation/navigation.component';
import { trigger, transition, animate, style, state } from '@angular/animations';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { AccountSettingsMenuComponent } from '../account-settings/account-settings-menu/account-settings-menu.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    animations: [
        trigger('openClose', [
            state('open', style({
                opacity: 1,
                visibility: 'visible',
                transform: 'translateX(0%)',
            })),
            state('closed', style({
                opacity: 0,
                visibility: 'hidden',
                transform: 'translateX(-100%)',
            })),
            transition('open <=> closed', [animate('0.4s ease-in')]),
        ]),
    ],
    imports: [CommonModule, MatToolbarModule, MatIconModule, NavigationComponent, AccountSettingsMenuComponent]
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isAuthorized: boolean = true;
  @Input() isExpanded: boolean = true;
  @Input() invertedLoginColor: boolean = false;
  @Input() public defaultThemeColor: string = 'blue';
  @Input() public activeSecondaryNavUrl: string = '';
  @Output() mobileScreen = new EventEmitter();
  @Output() notifyDesktopNavToggle = new EventEmitter();
  @Output() notifyMobileNavToggle = new EventEmitter();
  @Output() notifyCloseSecondaryNav = new EventEmitter();

  @ViewChild('header', { static: true }) headerElement: ElementRef = new ElementRef(HTMLElement);
  @ViewChild('mainNav', { static: false }) private mainNav!: NavigationComponent;

  public isMobileScreen: boolean = window.innerWidth <= 960;
  public openNavMenu: boolean = false;
  public disableAnimations: boolean = true;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    this.setMobileNav();
  }

  /**
   * Notify app level that the navigation has been toggled
   * @param event
   */
  public notifyDesktopNavToggleClick(event: MouseEvent | KeyboardEvent | TouchEvent): void {
    this.notifyDesktopNavToggle.emit(event);
  }

  /**
   * Notify app level to close the open settings navigation
   * */
  public closeSecondaryNav(): void {
    this.notifyCloseSecondaryNav.emit(event);
  }

  ngOnChanges(): void {
    this.setHeaderTransition();
  }

  /** Sets header transition after
   * initialization is done loading
   * */
  public setHeaderTransition(): void {
    this.breakpointObserver.observe(['(max-width: 960px)']).subscribe((state: BreakpointState) => {
      if (state.matches) {
        this.renderer.removeClass(this.headerElement.nativeElement, 'width-transition');
      } else {
        if (this.isAuthorized) {
          setTimeout(() => {
            this.renderer.addClass(this.headerElement.nativeElement, 'width-transition');
          }, 200);
        } else {
          setTimeout(() => {
            this.renderer.removeClass(this.headerElement.nativeElement, 'width-transition');
          }, 200);
        }
      }
    });
  }

  /**
   * Listen on resize and capture window inner width
   * @param event
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.isMobileScreen = window.innerWidth <= 960;
    this.setMobileNav();
    //if resize to desktop - toggle mobile nav to close nav, disable animations, destroy listener if mobile nav open
    if (!this.isMobileScreen && this.openNavMenu) {
      this.toggleMobileNav(event);
    }
  }

  /**
   * notify app component whether we are on mobile screen or not
   * @param event
   * */
  public setMobileNav(): void {
    this.mobileScreen.emit(this.isMobileScreen);

    //enable animations for mobile
    if (this.isMobileScreen) {
      setTimeout(() => {
        this.disableAnimations = false;
      }, 0);
    } else {
      this.disableAnimations = true;
    }
  }

  /**
   * toggile mobile menu and alerts app level that mobile menu opened or closed
   * @param event
   */
  public toggleMobileNav(event?: MouseEvent | KeyboardEvent | TouchEvent): void {
    this.openNavMenu = !this.openNavMenu;
    this.notifyMobileNavToggle.emit(this.openNavMenu);
    const saveHeaderBtn = this.headerElement.nativeElement.querySelector('#navToggleBtn');

    //if mobile nav open, call listener method in nav component -if mobile nav toggles to close, destroy listener
    setTimeout(() => {
      if (this.openNavMenu) {
        this.mainNav.setUpClickOutsideListener();
        this.mainNav.setUpTrapFocus();
      } else {
        this.mainNav.unlistenClickOut();
        this.mainNav.unlistenTrapFocus();

        //focus back on the toggle button for accessibility
        saveHeaderBtn.focus();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
